import { useEffect } from 'react'

import { Flex, Icon, type IconName, SlimButton } from 'components'

import * as S from './styles'

export interface ModalOptions {
  id?: number | string
  open: boolean
  sending?: boolean
}

export interface ModalProps {
  open: boolean
  setOpen: (isOpen: boolean) => void
  showClose?: boolean
  closeAction?: () => void
  titleIcon?: IconName
  title?: string | React.ReactElement
  description?: string | React.ReactElement
  children?: React.ReactNode
  showReturn?: boolean
  returnIcon?: React.ReactNode
  returnText?: string
  returnAction?: () => void
  returnDisabled?: boolean
  showAction?: boolean
  buttonIcon?: React.ReactNode
  buttonText?: string
  buttonAction?: () => void
  buttonDisabled?: boolean
  buttonVariant?: 'primary' | 'success' | 'danger'
  alignContent?: 'flex-start' | 'center' | 'flex-end'
  titleSize?: number
  descriptionSize?: number
  modalWidth?: number
  loading?: boolean
  actionBadge?: React.ReactNode
}

export const Modal: React.FC<ModalProps> = ({
  open,
  setOpen,
  showClose = true,
  closeAction = () => {
    setOpen(false)
  },
  title,
  titleIcon,
  description,
  children,
  showReturn = false,
  returnIcon = <Icon icon="undo" size="15" />,
  returnText = 'Voltar',
  returnAction = () => {
    setOpen(false)
  },
  returnDisabled = false,
  showAction = true,
  buttonIcon,
  buttonText,
  buttonAction = () => {
    setOpen(false)
  },
  buttonDisabled,
  buttonVariant = 'primary',
  alignContent = 'center',
  titleSize = 22,
  descriptionSize = 14,
  modalWidth,
  loading = false,
  actionBadge,
}) => {
  useEffect(() => {
    function handleCloseByKeyUpEscape(event: KeyboardEvent) {
      if (event.key === 'Escape' && open) {
        closeAction()
      }
    }

    window.addEventListener('keyup', handleCloseByKeyUpEscape)

    return () => {
      window.removeEventListener('keyup', handleCloseByKeyUpEscape)
    }
  }, [open, closeAction])

  const hasTitle = title && title !== ''

  return (
    <>
      {open && (
        <S.Wrapper
          data-testid="wrapper-modal"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              closeAction()
            }
          }}
        >
          <S.Content alignContent={alignContent} width={modalWidth}>
            {hasTitle || showClose ? (
              <S.Texts>
                <S.TitleRow>
                  {hasTitle ? (
                    typeof title === 'string' ? (
                      <S.TitleContainer>
                        {!!titleIcon && <Icon icon={titleIcon} size="18" />}
                        <S.Title textSize={titleSize}>{title}</S.Title>
                      </S.TitleContainer>
                    ) : (
                      <>{title}</>
                    )
                  ) : (
                    <div />
                  )}
                  {showClose ? (
                    <S.CloseButton onClick={closeAction}>
                      <Icon icon="close" size="25" />
                    </S.CloseButton>
                  ) : null}
                </S.TitleRow>
                <S.Divider />
                {!!description && (
                  <S.Description textSize={descriptionSize}>{description}</S.Description>
                )}
              </S.Texts>
            ) : null}
            {children}
            {showAction && (
              <>
                <S.Divider />
                <S.Actions>
                  {showReturn && (
                    <SlimButton
                      content={returnText}
                      data-testid="btn-return"
                      disabled={returnDisabled}
                      icon={returnIcon}
                      loading={loading}
                      onClick={returnAction}
                      variant="ghost"
                    />
                  )}
                  <div className="spacer" />
                  {buttonText && (
                    <Flex alignItems="center" gap="sm">
                      {actionBadge}
                      <SlimButton
                        content={buttonText}
                        data-testid="btn-action"
                        disabled={buttonDisabled}
                        icon={buttonIcon}
                        loading={loading}
                        onClick={buttonAction}
                        variant={buttonVariant}
                      />
                    </Flex>
                  )}
                </S.Actions>
              </>
            )}
          </S.Content>
        </S.Wrapper>
      )}
    </>
  )
}
